// RFC 7807 (problem+json) error contract — canonical `code` constants from
// spec §6.5, plus `EMAIL_NOT_VERIFIED` (Phase-0 addition for the
// self-registration flow: a login attempt against an unverified account is
// rejected with this code rather than a generic 403). `apps/api`'s
// `AllExceptionsFilter` (§5.2) is the sole producer of this shape; `apps/web`
// and any consumer of `@eskayscience/api-client` are the readers — both import
// the same `ErrorCode` union so a typo in either layer is a compile error,
// not a runtime string mismatch.

export const ErrorCode = {
  AUTH_INVALID_CREDENTIALS: 'AUTH_INVALID_CREDENTIALS',
  AUTH_TOKEN_EXPIRED: 'AUTH_TOKEN_EXPIRED',
  // P3-BUG-4 addition: no `Authorization` header/bearer token was sent at
  // all — distinct from `AUTH_TOKEN_EXPIRED` (a token WAS sent but is
  // malformed/expired/invalid/superseded) so clients/logs can disambiguate
  // "caller never authenticated" from "caller's session lapsed".
  AUTH_TOKEN_MISSING: 'AUTH_TOKEN_MISSING',
  FORBIDDEN_ROLE: 'FORBIDDEN_ROLE',
  SUBSCRIPTION_EXPIRED: 'SUBSCRIPTION_EXPIRED',
  RESOURCE_NOT_VISIBLE: 'RESOURCE_NOT_VISIBLE',
  SECTION_DEPTH_EXCEEDED: 'SECTION_DEPTH_EXCEEDED',
  SECTION_CYCLE: 'SECTION_CYCLE',
  LEVEL_HAS_ENROLLMENTS: 'LEVEL_HAS_ENROLLMENTS',
  BOARD_NOT_EMPTY: 'BOARD_NOT_EMPTY',
  DUPLICATE_CODE: 'DUPLICATE_CODE',
  ASSET_TOO_LARGE: 'ASSET_TOO_LARGE',
  ASSET_MIME_REJECTED: 'ASSET_MIME_REJECTED',
  IDEMPOTENCY_CONFLICT: 'IDEMPOTENCY_CONFLICT',
  RATE_LIMITED: 'RATE_LIMITED',
  // Phase-0 addition (self-registration flow, not in the original §6.5
  // table): login/resource access against a `PENDING_VERIFICATION` account.
  EMAIL_NOT_VERIFIED: 'EMAIL_NOT_VERIFIED',
  // P2-23 addition (single-active-session, HIGH PRIORITY security decision
  // 2026-08-17): a bearer access token, or the refresh-token family behind
  // it, whose `sid`/`familyId` no longer matches the user's CURRENT
  // `activeSessionId` — a newer login has since superseded this session.
  // Distinct from `AUTH_TOKEN_EXPIRED` (a genuinely malformed/expired/
  // signature-invalid token) so a client can tell "your session was
  // superseded elsewhere, please re-authenticate" apart from a routine
  // token-expiry re-auth, even though both currently force the same
  // re-login UX.
  SESSION_SUPERSEDED: 'SESSION_SUPERSEDED',
  // P3-04 addition (roster/enrollment surface, spec §6.2): `POST
  // /v1/students/:id/enrollments` against a `(studentId, levelId)` pair that
  // already has an `Enrollment` row (any status — the `@@unique([studentId,
  // levelId])` constraint on `prisma/schema.prisma`'s `Enrollment` model is
  // unconditional) — the caller should `PATCH /v1/enrollments/:id` (extend)
  // the existing row instead of creating a second one. None of the existing
  // 409 codes (`DUPLICATE_CODE` is exam-board/level code|slug uniqueness;
  // `LEVEL_HAS_ENROLLMENTS` is the level-delete guard) fit this case.
  DUPLICATE_ENROLLMENT: 'DUPLICATE_ENROLLMENT',
  // P3-04 addition: `POST /v1/students/import`'s CSV body exceeds
  // `ROSTER_IMPORT_MAX_BYTES` (`apps/api/src/services/roster.ts`) — the
  // import runs as a single Cloudflare Queue message (128KB platform cap),
  // so an oversized CSV must be rejected up front, not queued and left to
  // fail asynchronously with no caller-visible signal.
  IMPORT_PAYLOAD_TOO_LARGE: 'IMPORT_PAYLOAD_TOO_LARGE',
  // P3-05 addition (access grants + cohorts, spec §3.2/§4): `POST
  // /v1/cohorts/:id/members` against a `(cohortId, studentId)` pair that
  // already has a `CohortMember` row — the
  // `@@unique([cohortId, studentId])` constraint on `prisma/schema.prisma`'s
  // `CohortMember` model. Distinct 409 from `DUPLICATE_ENROLLMENT` (a
  // different resource/constraint entirely) rather than reused, per this
  // ticket's own AC ("409 DUPLICATE on conflict").
  DUPLICATE_COHORT_MEMBER: 'DUPLICATE_COHORT_MEMBER',
  // Lecture-notes-single-PDF backend task (2026-08-27): `POST
  // /v1/tabs/:id/resources` with `type: 'PDF'` against a `ResourceTab`
  // whose `kind === 'LECTURE_NOTES'` that already has a PDF resource — the
  // "Resources · Lecture Notes" tab supports EXACTLY ONE PDF (PO spec),
  // enforced server-side regardless of UI state
  // (`apps/api/src/services/resources.ts`'s `ResourceService.create`, plus
  // a `BEFORE INSERT` trigger backstop, `apps/api/migrations/
  // 0023_lecture_notes_single_pdf_trigger.sql`). Same "distinct 409 per
  // resource family" convention as `DUPLICATE_ENROLLMENT`/
  // `DUPLICATE_COHORT_MEMBER` above — frontend maps this to an
  // "ALREADY_EXISTS" style user-facing message distinct from
  // `ASSET_MIME_REJECTED`/`ASSET_TOO_LARGE`.
  LECTURE_NOTES_ALREADY_EXISTS: 'LECTURE_NOTES_ALREADY_EXISTS',
  // ADR-packages-entitlements §6.3 (Slice C): `PATCH /v1/packages/:id`
  // against a package that has ≥1 live `StudentPackage` row (status='ACTIVE'
  // AND expiresAt > now) whose body attempts to change the grant target
  // (`grantType`/`examBoardId`/`levelId`/`subjectId`) — repointing a package
  // that already has subscribers would silently change what those students
  // can open, with no expiry event and nothing on their own row to explain
  // it. Blocked; create a new package instead. Editing price/duration/status
  // is never blocked by this code (§6.3 — those affect future assignments
  // only).
  PACKAGE_HAS_SUBSCRIBERS: 'PACKAGE_HAS_SUBSCRIBERS',
  // ADR-packages-entitlements §6.3/§1.5 (Slice C follow-up, software-architect
  // ruling 2026-08-28): `POST /v1/students/:id/packages` (manual assignment)
  // against a `Package` whose `status` is `DRAFT` or `INACTIVE` — only
  // `ACTIVE` packages may be the target of a NEW grant. This is a write-path
  // (assignability) gate only; it must never be applied to the entitlement
  // READ path (`../access` resolver), which per ADR §1.5/§7.3 never filters
  // live entitlement by `Package.status` so an existing subscriber's access
  // survives a package later going INACTIVE.
  PACKAGE_NOT_ASSIGNABLE: 'PACKAGE_NOT_ASSIGNABLE',
  // Turnstile-captcha-on-register backend task (2026-08-27/29): `POST
  // /v1/auth/register`'s `turnstileToken` failed Cloudflare Turnstile
  // server-side `siteverify` (missing/invalid/expired/already-consumed
  // token, or `siteverify` itself unreachable — see
  // `apps/api/src/routes/auth.ts`'s register handler doc comment for the
  // deliberate fail-closed-on-outage rationale). Distinct from
  // `RATE_LIMITED` — this is "the human-check failed", not "too many
  // requests".
  CAPTCHA_FAILED: 'CAPTCHA_FAILED',
  // P4-11 (self-service account profile, `PATCH /v1/me/password`): a
  // Google-only account (`User.passwordHash === null`) attempting to SET a
  // password for the first time without having completed the required
  // step-up verification (`POST /v1/me/password/set/request` +
  // `/set/confirm` — reuses the existing `VerificationToken` + email infra,
  // see `apps/api/src/routes/me.ts`'s header). A live session alone is
  // deliberately NOT sufficient for this write (account-takeover risk if a
  // session/JWT were ever stolen) — distinct from `AUTH_INVALID_CREDENTIALS`
  // (that code is for a WRONG current password on an account that already
  // has one).
  SET_PASSWORD_VERIFICATION_REQUIRED: 'SET_PASSWORD_VERIFICATION_REQUIRED',
  // docs/ADR-multi-grant-packages.md §5.2 (S3): `POST /v1/packages` or
  // `PATCH /v1/packages/:id` whose body sets BOTH the legacy flat
  // grant-target shape (`grantType`/`examBoardId`/`levelId`/`subjectId`
  // directly on the body) AND the new `grants[]` array. Ambiguous — never
  // merged, never silently preferring one; the caller must resend with
  // exactly one shape.
  GRANT_SHAPE_CONFLICT: 'GRANT_SHAPE_CONFLICT',
  // Security review F2, 2026-09-02: `PATCH /v1/packages/:id` sending the
  // legacy flat single-target shape (`grantType`/`examBoardId`/`levelId`/
  // `subjectId`, no `grants[]`) against a package that PERSISTS more than
  // one `PackageGrant` row. The legacy quad can express only one grant, so
  // applying it would silently discard grants 2..N — a silent narrowing of
  // what students already assigned this package can access. Distinct from
  // `GRANT_SHAPE_CONFLICT` (that's "both shapes sent", this is "one shape
  // sent, but it structurally can't hold what's already there"); the
  // caller must resend the complete set via `grants[]` instead. Does not
  // fire when the package has 0 or 1 persisted grants (nothing to lose) or
  // when the body carries no grant-target fields at all (not a grant edit).
  MULTI_GRANT_SHAPE_REQUIRED: 'MULTI_GRANT_SHAPE_REQUIRED',
  // fix/invited-password-reset-activation: `POST /v1/users/:id/password-reset`
  // called by a TEACHER against a target whose `status === 'INVITED'` — an
  // invited account has no real password yet, so a TEACHER-initiated
  // "reset" would silently do nothing useful (login still gates on
  // `status !== 'ACTIVE'` afterward, same generic-looking failure as a
  // wrong password — this is the bug this code fixes). Only ADMIN may
  // activate an INVITED account through this endpoint (see that route's
  // handler comment); a TEACHER caller gets this instead. Deliberately does
  // NOT say "resend the invite" — there is no resend-invite route in this
  // codebase.
  ACCOUNT_NOT_ACTIVATED: 'ACCOUNT_NOT_ACTIVATED',
} as const;

export type ErrorCode = (typeof ErrorCode)[keyof typeof ErrorCode];
export const ERROR_CODE_VALUES = Object.values(ErrorCode) as ErrorCode[];

/**
 * The conventional HTTP status each code is expected to carry. Documentation
 * only — `AllExceptionsFilter` sets the real status on the thrown exception;
 * this map exists so tests/docs/clients have one place to check "is this
 * code normally a 4xx or a 409" without duplicating the mapping ad hoc.
 * `RESOURCE_NOT_VISIBLE` is deliberately 404, not 403 — spec §1 "fail
 * closed": absent grant, expired enrollment, or unpublished node must not
 * leak the existence of content.
 */
export const ERROR_CODE_STATUS: Record<ErrorCode, number> = {
  AUTH_INVALID_CREDENTIALS: 401,
  AUTH_TOKEN_EXPIRED: 401,
  AUTH_TOKEN_MISSING: 401,
  FORBIDDEN_ROLE: 403,
  SUBSCRIPTION_EXPIRED: 403,
  RESOURCE_NOT_VISIBLE: 404,
  SECTION_DEPTH_EXCEEDED: 422,
  SECTION_CYCLE: 422,
  LEVEL_HAS_ENROLLMENTS: 409,
  BOARD_NOT_EMPTY: 409,
  DUPLICATE_CODE: 409,
  ASSET_TOO_LARGE: 413,
  ASSET_MIME_REJECTED: 415,
  IDEMPOTENCY_CONFLICT: 409,
  RATE_LIMITED: 429,
  EMAIL_NOT_VERIFIED: 403,
  SESSION_SUPERSEDED: 401,
  DUPLICATE_ENROLLMENT: 409,
  IMPORT_PAYLOAD_TOO_LARGE: 413,
  DUPLICATE_COHORT_MEMBER: 409,
  LECTURE_NOTES_ALREADY_EXISTS: 409,
  PACKAGE_HAS_SUBSCRIBERS: 409,
  PACKAGE_NOT_ASSIGNABLE: 409,
  CAPTCHA_FAILED: 400,
  SET_PASSWORD_VERIFICATION_REQUIRED: 403,
  GRANT_SHAPE_CONFLICT: 400,
  MULTI_GRANT_SHAPE_REQUIRED: 400,
  ACCOUNT_NOT_ACTIVATED: 409,
};

/** RFC 7807 `application/problem+json` body shape (spec §6.5), extended with
 * the app-specific `code` (machine-matchable) and `requestId` (log/trace
 * correlation) fields on top of the base RFC members. */
export interface ProblemDetails {
  /** URI reference identifying the problem type, e.g.
   * `https://errors.example.com/subscription-expired`. */
  type: string;
  /** Short, human-readable summary of the problem type. */
  title: string;
  /** The HTTP status code for this occurrence, duplicated from the response
   * status line per RFC 7807 §3.1. */
  status: number;
  /** Machine-matchable code from {@link ErrorCode} — the field clients
   * should branch on, never `title`/`detail` (those are for humans/logs). */
  code: ErrorCode;
  /** Human-readable explanation specific to this occurrence. */
  detail?: string;
  /** URI reference identifying the specific occurrence (typically the
   * request path). */
  instance?: string;
  /** Correlation id propagated from `x-request-id` (§5.2
   * `RequestIdMiddleware`), echoed in logs and traces. */
  requestId?: string;
  /** `SESSION_SUPERSEDED`-only (session-superseded-UX backend task): ISO-8601
   * timestamp of when the NEWER login superseded this session — i.e. when the
   * session that is NOW `User.activeSessionId` began. Lets the client show
   * "you were signed in elsewhere at HH:MM" instead of a bare error code.
   * Deliberately carries NOTHING else about the other session/account: no
   * IP, no device/user-agent fingerprint, no session/user id. Omitted for
   * every other `code`. */
  supersededAt?: string;
}

/** Type guard for narrowing an unknown error-response body to
 * {@link ProblemDetails} — e.g. in an api-client interceptor. */
export function isProblemDetails(value: unknown): value is ProblemDetails {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.type === 'string' &&
    typeof v.title === 'string' &&
    typeof v.status === 'number' &&
    typeof v.code === 'string' &&
    (ERROR_CODE_VALUES as string[]).includes(v.code)
  );
}

// ─── makeProblem — canonical RFC 7807 body construction (P0-04b) ───────────
// Promoted from `apps/api/src/lib/problem.ts`'s scaffold-stage stopgap (the
// Worker scaffold couldn't reach into `packages/shared` for this yet — see
// that file's header). This is now the single source of truth: every
// producer of a `ProblemDetails` body (any framework, any runtime) should
// build it here rather than re-deriving `type`/`title` ad hoc. Pure string
// manipulation only — no `fetch`/DOM/Node API — so it is Workers-safe and
// usable from `apps/web`/`@eskayscience/api-client` too (e.g. constructing a
// fallback client-side problem body for a network failure).

const DEFAULT_PROBLEM_TYPE_BASE = 'https://errors.eskayscience.dev/';

function slugify(code: string): string {
  return code.toLowerCase().replace(/_/g, '-');
}

/** Title-cases a `SCREAMING_SNAKE_CASE` code into a short human summary,
 * e.g. `RESOURCE_NOT_VISIBLE` -> "Resource not visible". Codes that need a
 * more specific human title than this mechanical derivation can override it
 * per-call via `opts.title`. */
function humanTitle(code: string): string {
  const words = code.toLowerCase().split('_');
  return words
    .map((word, i) => (i === 0 ? word[0]!.toUpperCase() + word.slice(1) : word))
    .join(' ');
}

export interface MakeProblemOptions {
  detail?: string;
  instance?: string;
  requestId?: string;
  title?: string;
  /** Override the conventional status from {@link ERROR_CODE_STATUS} — rare;
   * only for a code deliberately reused at a non-default status. */
  status?: number;
  /** Override the `type` URI base (defaults to the platform's canonical
   * `https://errors.eskayscience.dev/`). */
  typeBase?: string;
  /** See {@link ProblemDetails.supersededAt} — only meaningful for
   * `SESSION_SUPERSEDED`; ignored (and should not be passed) for any other
   * code. */
  supersededAt?: string;
}

/**
 * Build a {@link ProblemDetails} body for a known {@link ErrorCode}.
 *
 * Two call shapes, both canonical:
 * - `makeProblem(code, detail, status?)` — the common case, a plain
 *   human-readable detail string (and an optional status override).
 * - `makeProblem(code, { detail, instance, requestId, title, status })` —
 *   the richer shape a request-handling layer (Hono `onError`, a Nest
 *   exception filter, ...) uses to thread request-scoped fields
 *   (`instance`/`requestId`) through without a second helper.
 */
export function makeProblem(
  code: ErrorCode,
  detailOrOptions?: string | MakeProblemOptions,
  status?: number,
): ProblemDetails {
  const opts: MakeProblemOptions =
    typeof detailOrOptions === 'string'
      ? { detail: detailOrOptions, status }
      : (detailOrOptions ?? {});

  return {
    type: `${opts.typeBase ?? DEFAULT_PROBLEM_TYPE_BASE}${slugify(code)}`,
    title: opts.title ?? humanTitle(code),
    status: opts.status ?? ERROR_CODE_STATUS[code],
    code,
    detail: opts.detail,
    instance: opts.instance,
    requestId: opts.requestId,
    supersededAt: opts.supersededAt,
  };
}
